import moment from "moment";
import { has } from "lodash";
import { from, Observable } from "rxjs";
import { store } from "..";
import { withProgress } from "../../components/loader/file-progress";
import { ResultWithProgress } from "../../components/loader/models";
import { API } from "../../services/http";
import { Pageable } from "../../services/pageable";
import { FetchableError, FetchableSuccess } from "../common/fetchable/helpers";
import { addFieldsToForm } from "../common/forms";
import { processErrorFromPromise } from "../errors/utils";
import { successNotificationFromPromise } from "../notifications/utils";
import { CryptoActions } from "./actions";
import { CryptoDTO } from "./models/dto";
export namespace CryptoService {
  const api = new API("chain");

  export function postFraudReport(
    report: CryptoDTO.Requests.CreateFraudReport.T
  ): Promise<CryptoDTO.Responses.FraudReportCreated.T> {
    return api
      .post(`fraud/reports`)
      .send(report)
      .then<CryptoDTO.Responses.FraudReportCreated.T>(
        (response) => response.body
      )
      .catch<CryptoDTO.Responses.FraudReportCreated.T>(processErrorFromPromise);
  }

  export function getAllFraudReport(
    pagination: Pageable.T<
      keyof CryptoDTO.Responses.GetSingleFraudReport.T
    > = {}
  ): Observable<
    FetchableError | FetchableSuccess<CryptoDTO.Responses.GetAllFraudReport.T>
  > {
    return from(
      api
        .get(`fraud/reports`)
        .query(Pageable.getQueryParam(pagination))
        .then<FetchableSuccess<CryptoDTO.Responses.GetAllFraudReport.T>>(
          (response) => ({
            status: "ready",
            data: response.body,
          })
        )
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getSingleFraudReport(
    id: CryptoDTO.Responses.GetSingleFraudReport.T["id"]
  ): Promise<CryptoDTO.Responses.GetSingleFraudReport.T> {
    const { singleFraudReportMap } = store.getState().crypto;

    if (
      has(singleFraudReportMap, id) &&
      singleFraudReportMap[id] !== "LOADING"
    ) {
      return Promise.resolve(
        singleFraudReportMap[id] as CryptoDTO.Responses.GetSingleFraudReport.T
      );
    }

    store.dispatch(CryptoActions.getSingleFraudReport(id));
    return api
      .get(`fraud/reports/${id}`)
      .then<CryptoDTO.Responses.GetSingleFraudReport.T>((response) => {
        store.dispatch(
          CryptoActions.setSingleFraudReport({
            id,
            result: response.body,
          })
        );
        return response.body;
      })
      .catch<CryptoDTO.Responses.GetSingleFraudReport.T>(
        processErrorFromPromise
      );
  }

  export function uploadDocumentToFraudReport(
    data: CryptoDTO.Requests.UploadDocument.T
  ): Observable<ResultWithProgress<CryptoDTO.Responses.UploadDocument.T>> {
    return withProgress(
      addFieldsToForm(api.post(`fraud/reports/${data.reportId}/upload`), data),
      (r) =>
        r
          .responseType("json")
          .then(successNotificationFromPromise("documents-uploaded"))
          .then((response) => response.body)
          .then((body) => {
            store.dispatch(CryptoActions.resetAllFraudReport());
            return body;
          })
          .catch<CryptoDTO.Responses.UploadDocument.T>(processErrorFromPromise)
    );
  }

  export function downloadDocumentToFraudReport(
    data: CryptoDTO.Requests.UploadDocument.T
  ): Observable<ResultWithProgress<CryptoDTO.Responses.UploadDocument.T>> {
    return withProgress(
      addFieldsToForm(api.post(`fraud/reports/${data.reportId}/upload`), data),
      (r) =>
        r
          .responseType("json")
          .then(successNotificationFromPromise("documents-uploaded"))
          .then((response) => response.body)
          .then((body) => {
            store.dispatch(CryptoActions.resetAllFraudReport());
            return body;
          })
          .catch<CryptoDTO.Responses.UploadDocument.T>(processErrorFromPromise)
    );
  }

  export function getFraudReportSummary(): Observable<
    | FetchableError
    | FetchableSuccess<CryptoDTO.Responses.GetFraudReportSummary.T>
  > {
    var dateOffset = 5 * 7 * 24 * 60 * 60 * 1000; //4 weeks
    var startDate = new Date();
    startDate.setTime(startDate.getTime() - dateOffset);

    return from(
      api
        .get(`fraud/reports/requests`)
        .query({ from: moment(startDate).format("yyyy-MM-DD") })
        .then<FetchableSuccess<CryptoDTO.Responses.GetFraudReportSummary.T>>(
          (response) => ({
            status: "ready",
            data: response.body,
          })
        )
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }
}
